import { useState, useEffect, useRef } from "react";
import Pusher from "pusher-js";
import { useAuth } from "../context/AuthContext";

const PUSHER_KEY = process.env.REACT_APP_PUSHER_KEY;
const PUSHER_CLUSTER = process.env.REACT_APP_PUSHER_CLUSTER || "mt1";


function formatTime(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "Ahora";
    return d.toLocaleTimeString("es-PE", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return "Ahora";
  }
}
export default function PanicAlertPopup() {
  const { user } = useAuth();

  const [queue, setQueue] = useState([]); // alertas en espera
  const [current, setCurrent] = useState(null); // alerta visible
  const [closing, setClosing] = useState(false);
  const [entering, setEntering] = useState(false);

  // Ref para acceder al current dentro del closure de Pusher
  const currentRef = useRef(null);
  useEffect(() => {
    currentRef.current = current;
  }, [current]);

  // ── Conexión Pusher ──────────────────────────────────────
  useEffect(() => {
    if (!user || !PUSHER_KEY) return;

    const pusher = new Pusher(PUSHER_KEY, {
      cluster: PUSHER_CLUSTER,
    });

    const channel = pusher.subscribe("alarmas-panico");

    // Escucha el evento que dispara Laravel
    channel.bind("nueva-alerta", (data) => {
      // ← AGREGAR esta línea para ver qué llega exactamente
      console.log("Pusher data recibida:", data);

      // Los datos pueden venir dentro de data.alerta
      const alerta = data.alerta || data;
      addToQueue(alerta);
    });

    return () => {
      channel.unbind_all();
      pusher.unsubscribe("alarmas-panico");
      pusher.disconnect();
    };
  }, [user]);

  // ── Agrega a cola o muestra directo ─────────────────────
  const addToQueue = (alertData) => {
    if (!currentRef.current) {
      setCurrent(alertData);
    } else {
      setQueue((q) => [...q, alertData]);
    }
  };

  // ── Avanza a la siguiente alerta ─────────────────────────
  const showNext = () => {
    setQueue((q) => {
      if (q.length === 0) {
        setCurrent(null);
        return [];
      }
      const [next, ...rest] = q;
      setEntering(true);
      setTimeout(() => setEntering(false), 400);
      setCurrent(next);
      return rest;
    });
  };

  // ── Marcar atendida en Laravel ───────────────────────────
  const markAttended = async (id) => {
    try {
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token") || "";
      const base = process.env.REACT_APP_API_URL || "http://localhost:8000/api";
      await fetch(`${base}/alarma-panico/${id}/atender`, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (err) {
      console.warn("Error marcando atendida:", err);
    }
  };

  // ── Cerrar popup ─────────────────────────────────────────
  const dismiss = async (attended = false) => {
    if (closing) return;
    if (attended && current?.id) await markAttended(current.id);
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      showNext();
    }, 350);
  };

  if (!user) return null;

  const pendingCount = queue.length;

  return (
    <>
      <style>{CSS}</style>

      {/* ── Botón flotante SOS ── */}
      <button className="panic-sos-btn" title="Alertas de pánico">
        🚨
        <span className="panic-sos-label">SOS</span>
        {(pendingCount > 0 || current) && (
          <span className="panic-queue-badge">
            {current ? pendingCount + 1 : pendingCount}
          </span>
        )}
      </button>

      {/* ── Popup ── */}
      {current && (
        <>
          <div
            className={`panic-overlay ${closing ? "panic-fade-out" : "panic-fade-in"}`}
          />

          <div
            className={`panic-modal ${
              closing
                ? "panic-slide-out"
                : entering
                  ? "panic-slide-next"
                  : "panic-slide-in"
            }`}
          >
            {/* Header rojo */}
            <div className="panic-top">
              <div className="panic-top-left">
                <span className="panic-siren">🚨</span>
                <div>
                  <div className="panic-title">ALERTA DE PÁNICO</div>
                  <div className="panic-time">
                    Recibida a las {formatTime(current.timestamp)}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {pendingCount > 0 && (
                  <div className="panic-pending-pill">
                    +{pendingCount} pendiente{pendingCount > 1 ? "s" : ""}
                  </div>
                )}
                <div className="panic-blink-dot" />
              </div>
            </div>

            {/* Datos */}
            <div className="panic-body">
              <div className="panic-card">
                <span className="panic-card-icon">👤</span>
                <div>
                  <div className="panic-card-label">Conductor</div>
                  <div className="panic-card-value">{current.conductor}</div>
                </div>
              </div>

              <div className="panic-card">
                <span className="panic-card-icon">🚗</span>
                <div>
                  <div className="panic-card-label">Vehículo / Placa</div>
                  <div className="panic-card-value">{current.vehiculo}</div>
                  {current.placa && (
                    <span className="panic-badge">{current.placa}</span>
                  )}
                </div>
              </div>

              <div className="panic-card">
                <span className="panic-card-icon">📍</span>
                <div>
                  <div className="panic-card-label">Última Ubicación</div>
                  <div className="panic-card-value">{current.direccion}</div>
                  {current.lat && (
                    <div className="panic-coords">
                      {current.lat}, {current.lng}
                    </div>
                  )}
                </div>
              </div>

              {current.telefono && (
                <div className="panic-card">
                  <span className="panic-card-icon">📞</span>
                  <div>
                    <div className="panic-card-label">Teléfono</div>
                    <div className="panic-card-value">{current.telefono}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Botones */}
            <div className="panic-footer">
              {current.lat && (
                <a
                  className="panic-btn-map"
                  href={`https://maps.google.com/?q=${current.lat},${current.lng}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  📍 Ver en Mapa
                </a>
              )}
              <button
                className="panic-btn-attend"
                onClick={() => dismiss(true)}
              >
                ✅ Atendida
                {pendingCount > 0 && (
                  <span className="panic-next-label"> → siguiente</span>
                )}
              </button>
              <button
                className="panic-btn-close"
                onClick={() => dismiss(false)}
              >
                ✕
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ── Estilos ──────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@600;700&display=swap');

  @keyframes panic-pulse {
    0%   { box-shadow: 0 0 0 0 rgba(220,38,38,0.7); }
    70%  { box-shadow: 0 0 0 14px rgba(220,38,38,0); }
    100% { box-shadow: 0 0 0 0 rgba(220,38,38,0); }
  }
  @keyframes panic-fade-in   { from{opacity:0} to{opacity:1} }
  @keyframes panic-fade-out  { from{opacity:1} to{opacity:0} }
  @keyframes panic-slide-in  {
    from { opacity:0; transform:translate(-50%,-50%) translateY(-30px) scale(0.95); }
    to   { opacity:1; transform:translate(-50%,-50%) translateY(0) scale(1); }
  }
  @keyframes panic-slide-out {
    from { opacity:1; transform:translate(-50%,-50%) translateY(0) scale(1); }
    to   { opacity:0; transform:translate(-50%,-50%) translateY(-20px) scale(0.95); }
  }
  @keyframes panic-slide-next {
    from { opacity:0; transform:translate(-50%,-50%) translateX(40px) scale(0.97); }
    to   { opacity:1; transform:translate(-50%,-50%) translateX(0) scale(1); }
  }
  @keyframes panic-blink     { 0%,100%{opacity:1} 50%{opacity:0.15} }
  @keyframes panic-siren     { 0%,100%{opacity:1} 50%{opacity:0.4}  }
  @keyframes panic-badge-pop {
    0%  {transform:scale(0)} 70%{transform:scale(1.3)} 100%{transform:scale(1)}
  }

  .panic-sos-btn {
    display:none;
  }
  .panic-sos-btn:hover  { transform:scale(1.1); }
  .panic-sos-btn:active { transform:scale(0.93); }
  .panic-sos-label {
    font-size:10px; font-weight:700; letter-spacing:1px; margin-top:-2px;
  }
  .panic-queue-badge {
    position:absolute; top:-4px; right:-4px;
    background:#fbbf24; color:#111;
    font-size:11px; font-weight:800;
    min-width:20px; height:20px; border-radius:10px;
    display:flex; align-items:center; justify-content:center;
    padding:0 5px; border:2px solid #991b1b;
    animation:panic-badge-pop 0.3s ease;
  }
  .panic-pending-pill {
    background:#fbbf24; color:#111;
    font-size:11px; font-weight:700;
    padding:3px 10px; border-radius:20px; white-space:nowrap;
    animation:panic-badge-pop 0.3s ease;
  }

  .panic-overlay {
    position:fixed; inset:0;
    background:rgba(0,0,0,0.72);
    z-index:9998; backdrop-filter:blur(3px);
    pointer-events:none;
  }
  .panic-fade-in  { animation:panic-fade-in  0.3s ease forwards; }
  .panic-fade-out { animation:panic-fade-out 0.35s ease forwards; }

  .panic-modal {
    position:fixed; top:50%; left:50%;
    transform:translate(-50%,-50%);
    z-index:10000; width:min(480px,92vw);
    background:#161b22; border-radius:16px; overflow:hidden;
    border:1px solid #ef4444;
    box-shadow:0 0 60px rgba(239,68,68,0.3),0 20px 60px rgba(0,0,0,0.6);
  }
  .panic-slide-in   { animation:panic-slide-in   0.35s cubic-bezier(.22,1,.36,1) forwards; }
  .panic-slide-out  { animation:panic-slide-out  0.35s ease forwards; }
  .panic-slide-next { animation:panic-slide-next 0.35s cubic-bezier(.22,1,.36,1) forwards; }

  .panic-top {
    background:linear-gradient(135deg,#7f1d1d,#991b1b);
    padding:18px 20px; display:flex;
    justify-content:space-between; align-items:center;
  }
  .panic-top-left { display:flex; align-items:center; gap:14px; }
  .panic-siren    { font-size:36px; animation:panic-siren 0.8s infinite; }
  .panic-title    {
    font-family:'Rajdhani',sans-serif;
    font-size:22px; font-weight:700; color:#fff; letter-spacing:2px;
  }
  .panic-time      { color:#fca5a5; font-size:12px; margin-top:2px; }
  .panic-blink-dot {
    width:14px; height:14px; border-radius:50%;
    background:#fbbf24; box-shadow:0 0 12px #fbbf24;
    animation:panic-blink 0.6s infinite;
  }

  .panic-body { padding:16px 20px; display:flex; flex-direction:column; gap:10px; }
  .panic-card {
    display:flex; align-items:flex-start; gap:14px;
    background:#0d1117; border:1px solid #21262d;
    border-radius:10px; padding:12px 14px;
  }
  .panic-card-icon  { font-size:22px; margin-top:2px; flex-shrink:0; }
  .panic-card-label { font-size:11px; color:#64748b; text-transform:uppercase; letter-spacing:1px; }
  .panic-card-value { font-size:15px; font-weight:600; color:#f1f5f9; margin-top:2px; }
  .panic-badge {
    display:inline-block; margin-top:4px;
    background:#1e3a5f; color:#60a5fa;
    font-size:11px; font-weight:700;
    padding:2px 8px; border-radius:4px; letter-spacing:1px;
  }
  .panic-coords { font-size:11px; color:#475569; margin-top:3px; font-family:monospace; }

  .panic-footer {
    display:flex; gap:8px; padding:14px 20px;
    border-top:1px solid #21262d; background:#0d1117;
  }
  .panic-btn-map {
    flex:1; padding:10px; border-radius:8px;
    border:1px solid #1d4ed8; background:#1e3a5f; color:#93c5fd;
    font-weight:600; font-size:13px; cursor:pointer;
    text-decoration:none; text-align:center; transition:background 0.2s;
  }
  .panic-btn-attend {
    flex:1; padding:10px; border-radius:8px; border:none;
    background:linear-gradient(135deg,#15803d,#166534);
    color:#fff; font-weight:600; font-size:13px;
    cursor:pointer; transition:opacity 0.2s;
  }
  .panic-btn-close {
    width:40px; border-radius:8px;
    border:1px solid #374151; background:#1f2937; color:#9ca3af;
    font-weight:700; font-size:16px; cursor:pointer; transition:background 0.2s;
  }
  .panic-btn-map:hover    { background:#1e40af; }
  .panic-btn-attend:hover { opacity:0.85; }
  .panic-btn-close:hover  { background:#374151; }
  .panic-next-label       { font-size:11px; opacity:0.8; }
`;
